var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState, } from 'react';
import { ThemeConsumer } from 'styled-components';
import url from 'url';
import styled from '../utils/styled-components';
/**
 * Returns source element for the picture element
 */
var toSourceElement = function (props, index) { return (React.createElement("source", __assign({ key: index }, props))); };
/**
 * Some Browsers have native lazy loading.
 * `loading="lazy"`
 * https://web.dev/native-lazy-loading/
 */
export var browserSupportsNativeLazyLoading = function () {
    return typeof HTMLImageElement !== 'undefined' && 'loading' in HTMLImageElement.prototype;
};
/**
 *  Builds a source element
 */
var buildSourceForBreakpoint = function (src, srcRetina, breakpoint, key) {
    var srcSet = srcRetina ? src + " 1x, " + srcRetina + " 2x" : src + " 1x";
    var sourceAttributes = { key: key, srcSet: srcSet };
    if (breakpoint) {
        sourceAttributes.media = "(min-width: " + breakpoint + ")";
    }
    return React.createElement("source", __assign({}, sourceAttributes));
};
/**
 * Builds source elements depending on theme breakpoints
 */
var buildSourcesForBreakpoints = function (src, theme) {
    var sources = [];
    var keyIndex = 0;
    if (theme && src.desktop) {
        var source_1 = buildSourceForBreakpoint(src.desktop, src.desktopRetina, theme.global.breakpoints.desktop, (keyIndex += 1));
        sources.push(source_1);
    }
    if (theme && src.tablet) {
        var source_2 = buildSourceForBreakpoint(src.tablet, src.tabletRetina, theme.global.breakpoints.tablet, (keyIndex += 1));
        sources.push(source_2);
    }
    if (theme && src.mobile) {
        var source_3 = buildSourceForBreakpoint(src.mobile, src.mobileRetina, theme.global.breakpoints.mobile, (keyIndex += 1));
        sources.push(source_3);
    }
    var source = buildSourceForBreakpoint(src.default, src.defaultRetina, undefined, (keyIndex += 1));
    sources.push(source);
    return sources;
};
var Status;
(function (Status) {
    Status[Status["pending"] = 0] = "pending";
    Status[Status["loaded"] = 1] = "loaded";
    Status[Status["error"] = 2] = "error";
})(Status || (Status = {}));
/**
 * Responsive Image
 */
var Image = function (_a) {
    var alt = _a.alt, className = _a.className, source = _a.source, src = _a.src, title = _a.title, subtitle = _a.subtitle, fallback = _a.fallback, loadingComponent = _a.loadingComponent, keepPrevLoadedImage = _a.keepPrevLoadedImage, props = __rest(_a, ["alt", "className", "source", "src", "title", "subtitle", "fallback", "loadingComponent", "keepPrevLoadedImage"]);
    var _b = useState(Status.pending), status = _b[0], setStatus = _b[1];
    var _c = useState(false), prevImgIsAvailable = _c[0], setPrevImgIsAvailable = _c[1];
    useEffect(function () {
        // If source changes reset status so the image doesn't get blocked after triggering a fallback
        if (status !== Status.pending) {
            setStatus(Status.pending);
        }
    }, [src, source]);
    useEffect(function () {
        // Notice component about the availability of a previous loaded image
        if (status !== Status.pending) {
            setPrevImgIsAvailable(status === Status.loaded || status !== Status.error);
        }
    }, [status]);
    var imgSrc = src.toString();
    var imgProps = __assign(__assign({}, props), { alt: alt, className: className +
            (!(keepPrevLoadedImage && prevImgIsAvailable) && status === Status.pending && loadingComponent
                ? ' loading'
                : ''), src: imgSrc, title: title,
        subtitle: subtitle, onLoad: function () { return setStatus(Status.loaded); }, onError: function () { return setStatus(Status.error); } });
    if (fallback && status === Status.error) {
        return fallback();
    }
    var showLoading = function () {
        return !(keepPrevLoadedImage && prevImgIsAvailable) &&
            loadingComponent &&
            status === Status.pending &&
            loadingComponent();
    };
    if (source) {
        var sourceElements = source.map(toSourceElement);
        return (React.createElement(React.Fragment, null,
            showLoading(),
            React.createElement("picture", null,
                sourceElements,
                React.createElement("img", __assign({}, imgProps)))));
    }
    if (typeof src === 'object') {
        return (React.createElement(React.Fragment, null,
            showLoading(),
            React.createElement(ThemeConsumer, null, function (theme) {
                var sources = buildSourcesForBreakpoints(src, theme);
                return (React.createElement("picture", null,
                    sources,
                    React.createElement("img", __assign({}, imgProps, { src: src.default }))));
            })));
    }
    return (React.createElement(React.Fragment, null,
        showLoading(),
        React.createElement("img", __assign({}, imgProps))));
};
var StyledImage = styled(Image)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: block;\n  width: 100%;\n  height: auto;\n\n  &.loading {\n    visibility: hidden;\n    height: 0;\n    width: 0;\n  }\n"], ["\n  display: block;\n  width: 100%;\n  height: auto;\n\n  &.loading {\n    visibility: hidden;\n    height: 0;\n    width: 0;\n  }\n"])));
function getImageUrlWithOptions(src, width, height, face, fit) {
    if (face === void 0) { face = 'face'; }
    if (fit === void 0) { fit = 'fill'; }
    var parsedUrl = url.parse(src, true);
    parsedUrl.query = __assign(__assign({}, parsedUrl.query), { w: String(width), h: String(height), f: face, fit: fit });
    return url.format(parsedUrl);
}
export { getImageUrlWithOptions, StyledImage as Image };
export default StyledImage;
var templateObject_1;
//# sourceMappingURL=index.js.map